import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import { enqueueSnackbar } from "notistack";
import Breadcrumb from "../../components/Common/Breadcrumb";
import {
  addCompanyProfileBankAccount,
  deleteCompanyProfileBankAccount,
  getAllBanks,
  getCurrentCompanyProfile,
  updateCurrentCompanyProfileMultipart,
  updateCompanyProfileBankAccount,
} from "../../services/ValitanApiService";

const BRAND_BLUE = "#0147C8";
const EMPTY_BANK_ACCOUNT = {
  id: null,
  bankId: "",
  bankName: "",
  accountName: "",
  accountNumber: "",
};

const DEFAULT_PROFILE = {
  id: null,
  companyName: "",
  poBox: "",
  city: "",
  tin: "",
  vrn: "",
  vat: "18",
  email: "",
  phonePrimary: "",
  phoneSecondary: "",
  physicalAddress: "",
  logoDataUrl: "",
  bankAccounts: [{ ...EMPTY_BANK_ACCOUNT }],
};

const companyFields = [
  { key: "companyName", label: "Company Name", md: 8 },
  { key: "poBox", label: "P.O. Box", md: 6 },
  { key: "city", label: "City", md: 6 },
  { key: "tin", label: "TIN", md: 4 },
  { key: "vrn", label: "VRN", md: 4 },
  { key: "vat", label: "VAT (%)", type: "number", md: 4 },
  { key: "email", label: "Email", type: "email", md: 6 },
  { key: "phonePrimary", label: "Phone 1", md: 3 },
  { key: "phoneSecondary", label: "Phone 2", md: 3 },
  { key: "physicalAddress", label: "Physical Address", type: "textarea", rows: 2, md: 12 },
];

const normalizeProfileResponse = (dto = {}) => {
  const bankAccounts = dto.bankAccounts || dto.bank_accounts || [];
  const normalizedAccounts = bankAccounts.map((account) => ({
    id: account.id || null,
    bankId: account.bankId || account.bank_id || "",
    bankName: account.bankName || account.bank_name || "",
    accountName: account.accountName || account.account_name || "",
    accountNumber: account.accountNumber || account.account_number || "",
  }));

  return {
    id: dto.id || null,
    companyName: dto.companyName || dto.company_name || "",
    poBox: dto.poBox || dto.po_box || "",
    city: dto.city || "",
    tin: String(dto.tin || "").replace(/^TIN:\s*/i, "").trim(),
    vrn: String(dto.vrn || "").replace(/^VRN:\s*/i, "").trim(),
    vat: dto.vat !== null && dto.vat !== undefined ? String(dto.vat) : "18",
    email: dto.email || "",
    phonePrimary: dto.phonePrimary || dto.phone_primary || "",
    phoneSecondary: dto.phoneSecondary || dto.phone_secondary || "",
    physicalAddress: dto.physicalAddress || dto.physical_address || "",
    logoDataUrl: dto.logo || "",
    bankAccounts: normalizedAccounts.length > 0 ? normalizedAccounts : [{ ...EMPTY_BANK_ACCOUNT }],
  };
};

const CompanyProfile = () => {
  document.title = "Company Profile | Valitan";

  const [profile, setProfile] = useState(DEFAULT_PROFILE);
  const [bankOptions, setBankOptions] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [persistedBankAccountIds, setPersistedBankAccountIds] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  const [validationErrors, setValidationErrors] = useState({ fields: {}, bankAccounts: [] });
  const logoInputRef = useRef(null);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [banksResp, profileResp] = await Promise.all([
          getAllBanks(0, 300),
          getCurrentCompanyProfile().catch((error) => {
            // No profile yet is fine — keep empty form for first-time create/save.
            if (error?.response?.status === 404) return null;
            throw error;
          }),
        ]);

        const banks = banksResp?.data?.data?.content || [];
        setBankOptions(banks);

        const profileData = profileResp?.data?.data || profileResp?.data || null;
        if (profileData && (profileData.id || profileData.company_name || profileData.companyName)) {
          const normalized = normalizeProfileResponse(profileData);
          setProfile(normalized);
          setPersistedBankAccountIds(
            (normalized.bankAccounts || []).map((a) => a.id).filter(Boolean)
          );
          if (normalized.logoDataUrl) {
            setLogoPreviewUrl(normalized.logoDataUrl);
          }
        }
      } catch (error) {
        console.error("Error loading company profile:", error);
        enqueueSnackbar("Failed to load company profile.", { variant: "error" });
      }
    };

    loadInitialData();
  }, []);

  const handleChange = (field, value) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
    setValidationErrors((prev) => ({
      ...prev,
      fields: { ...prev.fields, [field]: "" },
    }));
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      enqueueSnackbar("Please upload a valid image file.", { variant: "warning" });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      enqueueSnackbar("Logo should be less than 2MB.", { variant: "warning" });
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setLogoFile(file);
    setLogoPreviewUrl((prev) => {
      if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
      return objectUrl;
    });
  };

  const removeLogo = () => {
    setProfile((prev) => ({ ...prev, logoDataUrl: "" }));
    setLogoFile(null);
    setLogoPreviewUrl((prev) => {
      if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
      return "";
    });
  };

  const openLogoPicker = () => {
    logoInputRef.current?.click();
  };

  const addBankAccount = () => {
    setProfile((prev) => ({
      ...prev,
      bankAccounts: [...(prev.bankAccounts || []), { ...EMPTY_BANK_ACCOUNT }],
    }));
    setValidationErrors((prev) => ({ ...prev, bankAccounts: [] }));
  };

  const removeBankAccount = (index) => {
    setProfile((prev) => {
      const next = [...(prev.bankAccounts || [])];
      if (next.length <= 1) return prev;
      next.splice(index, 1);
      return { ...prev, bankAccounts: next };
    });
    setValidationErrors((prev) => ({ ...prev, bankAccounts: [] }));
  };

  const handleBankAccountChange = (index, field, value) => {
    setProfile((prev) => {
      const next = [...(prev.bankAccounts || [])];
      if (field === "bankId") {
        const selectedBank = bankOptions.find((b) => b.id === value);
        next[index] = {
          ...next[index],
          bankId: value,
          bankName: selectedBank?.name || "",
        };
      } else {
        next[index] = { ...next[index], [field]: value };
      }
      return { ...prev, bankAccounts: next };
    });
    setValidationErrors((prev) => {
      const nextBankErrors = [...(prev.bankAccounts || [])];
      const rowErrors = { ...(nextBankErrors[index] || {}) };
      rowErrors[field] = "";
      nextBankErrors[index] = rowErrors;
      return { ...prev, bankAccounts: nextBankErrors };
    });
  };

  const validateProfile = () => {
    const fields = {};
    const bankAccounts = [];

    if (!profile.companyName?.trim()) fields.companyName = "Company name is required.";
    if (!profile.tin?.trim()) fields.tin = "TIN is required.";
    if (!profile.email?.trim()) {
      fields.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email.trim())) {
      fields.email = "Enter a valid email address.";
    }

    if (profile.vat !== "" && profile.vat !== null && profile.vat !== undefined) {
      const vatNumber = Number(profile.vat);
      if (Number.isNaN(vatNumber) || vatNumber < 0 || vatNumber > 100) {
        fields.vat = "VAT must be between 0 and 100.";
      }
    }

    (profile.bankAccounts || []).forEach((account, index) => {
      const hasAnyValue = account.accountName || account.accountNumber || account.bankId;
      if (!hasAnyValue) return;

      const rowErrors = {};
      if (!account.accountName?.trim()) rowErrors.accountName = "Account name is required.";
      if (!account.accountNumber?.trim()) rowErrors.accountNumber = "Account number is required.";
      if (!account.bankId) rowErrors.bankId = "Bank is required.";
      if (Object.keys(rowErrors).length > 0) bankAccounts[index] = rowErrors;
    });

    return {
      fields,
      bankAccounts,
      hasErrors: Object.keys(fields).length > 0 || bankAccounts.some((row) => row && Object.keys(row).length > 0),
    };
  };

  const syncBankAccounts = async (companyProfileId) => {
    const currentAccounts = profile.bankAccounts || [];
    const currentIds = currentAccounts.map((a) => a.id).filter(Boolean);
    const nextAccounts = [...currentAccounts];

    const deletedIds = persistedBankAccountIds.filter((id) => !currentIds.includes(id));
    if (deletedIds.length > 0) {
      await Promise.all(
        deletedIds.map((id) => deleteCompanyProfileBankAccount(companyProfileId, id))
      );
    }

    for (let i = 0; i < currentAccounts.length; i += 1) {
      const account = currentAccounts[i];
      if (!account.bankId || !account.accountName || !account.accountNumber) continue;

      const payload = {
        bankId: account.bankId,
        accountName: account.accountName,
        accountNumber: account.accountNumber,
        displayOrder: i,
      };

      if (account.id) {
        await updateCompanyProfileBankAccount(companyProfileId, account.id, payload);
      } else {
        const response = await addCompanyProfileBankAccount(companyProfileId, payload);
        const createdAccount = response?.data?.data || {};
        nextAccounts[i] = {
          ...nextAccounts[i],
          id: createdAccount.id || nextAccounts[i]?.id || null,
        };
      }
    }

    return nextAccounts;
  };

  const saveProfile = async (event) => {
    event.preventDefault();

    const errors = validateProfile();
    setValidationErrors({ fields: errors.fields, bankAccounts: errors.bankAccounts });
    if (errors.hasErrors) {
      enqueueSnackbar("Please fix highlighted fields.", { variant: "warning" });
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        companyName: profile.companyName,
        poBox: profile.poBox,
        city: profile.city,
        tin: profile.tin,
        vrn: profile.vrn,
        vat: profile.vat ? Number(profile.vat) : null,
        email: profile.email,
        phonePrimary: profile.phonePrimary,
        phoneSecondary: profile.phoneSecondary,
        physicalAddress: profile.physicalAddress,
        logo: profile.logoDataUrl,
      };

      const saveResp = await updateCurrentCompanyProfileMultipart(payload, logoFile);
      let companyProfileId = saveResp?.data?.data?.id || profile.id;

      if (!companyProfileId) {
        throw new Error("Unable to determine company profile ID after save.");
      }

      const syncedAccounts = await syncBankAccounts(companyProfileId);
      const savedProfile = normalizeProfileResponse(saveResp?.data?.data || {});
      setProfile((prev) => ({
        ...prev,
        ...savedProfile,
        id: companyProfileId,
        bankAccounts: syncedAccounts.length > 0 ? syncedAccounts : [{ ...EMPTY_BANK_ACCOUNT }],
      }));
      setPersistedBankAccountIds(syncedAccounts.map((a) => a.id).filter(Boolean));
      setValidationErrors({ fields: {}, bankAccounts: [] });
      setLogoFile(null);

      enqueueSnackbar("Company profile saved successfully.", { variant: "success" });
    } catch (error) {
      console.error("Error saving company profile:", error);
      const message = error?.response?.data?.message || "Failed to save company profile.";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => () => {
    if (logoPreviewUrl && logoPreviewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(logoPreviewUrl);
    }
  }, [logoPreviewUrl]);

  const headerSummary = useMemo(() => {
    const tinValue = String(profile.tin || "")
      .replace(/^TIN:\s*/i, "")
      .trim();
    const vrnValue = String(profile.vrn || "-")
      .replace(/^VRN:\s*/i, "")
      .trim() || "-";
    return `${profile.companyName} | TIN: ${tinValue || "-"} | VRN: ${vrnValue}`;
  }, [profile.companyName, profile.tin, profile.vrn]);

  const renderField = ({ key, label, type = "text", rows = 1, md = 6 }) => (
    <Col md={md} key={key}>
      <FormGroup>
        <Label>{label}</Label>
        <Input
          type={type}
          rows={rows}
          value={profile[key] || ""}
          onChange={(e) => handleChange(key, e.target.value)}
          invalid={Boolean(validationErrors.fields[key])}
        />
        {validationErrors.fields[key] ? <FormFeedback>{validationErrors.fields[key]}</FormFeedback> : null}
      </FormGroup>
    </Col>
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Settings" breadcrumbItem="Company profile" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h5 className="mb-0">Company Details</h5>
                </CardHeader>
                <CardBody>
                  <Form onSubmit={saveProfile}>
                    <Row className="g-3">
                      <Col md={12}>
                        <FormGroup>
                          <Label>Company Logo</Label>
                          <Input
                            innerRef={logoInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            style={{ display: "none" }}
                          />
                          <div className="d-flex align-items-center gap-3">
                            <button
                              type="button"
                              onClick={openLogoPicker}
                              className="border rounded d-flex align-items-center justify-content-center bg-light"
                              style={{ width: "120px", height: "120px", cursor: "pointer" }}
                              title="Update logo"
                            >
                              {logoPreviewUrl || profile.logoDataUrl ? (
                                <img
                                  src={logoPreviewUrl || profile.logoDataUrl}
                                  alt="Company Logo"
                                  style={{ maxWidth: "110px", maxHeight: "110px", objectFit: "contain" }}
                                />
                              ) : (
                                <div className="text-muted text-center">
                                  <i className="ri-image-line font-size-20 d-block mb-1" />
                                  <small>Logo</small>
                                </div>
                              )}
                            </button>
                            <div className="d-flex flex-column gap-2">
                              <Button type="button" color="light" size="sm" onClick={openLogoPicker}>
                                <i className="ri-edit-2-line me-1" />
                                Edit
                              </Button>
                              {logoPreviewUrl || profile.logoDataUrl ? (
                                <Button type="button" size="sm" color="danger" onClick={removeLogo} title="Remove logo">
                                  <i className="ri-close-line" />
                                </Button>
                              ) : null}
                            </div>
                          </div>
                          <small className="text-muted d-block mt-2">Supported: image files up to 2MB.</small>
                        </FormGroup>
                      </Col>

                      {companyFields.map(renderField)}

                      <Col md={12}>
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="mb-0">Bank Accounts</h6>
                          <Button
                            type="button"
                            size="sm"
                            style={{ backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE }}
                            onClick={addBankAccount}
                          >
                            <i className="ri-add-line me-1" />
                            Add Account
                          </Button>
                        </div>
                      </Col>

                      {(profile.bankAccounts || []).map((account, index) => (
                        <React.Fragment key={`bank-account-${index}`}>
                          <Col md={12}>
                            <small className="text-muted">Account {index + 1}</small>
                          </Col>
                          <Col md={3}>
                            <FormGroup>
                              <Label>Account Name</Label>
                              <Input
                                type="text"
                                value={account.accountName || ""}
                                onChange={(e) =>
                                  handleBankAccountChange(index, "accountName", e.target.value)
                                }
                                invalid={Boolean(validationErrors.bankAccounts[index]?.accountName)}
                              />
                              {validationErrors.bankAccounts[index]?.accountName ? (
                                <FormFeedback>{validationErrors.bankAccounts[index]?.accountName}</FormFeedback>
                              ) : null}
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup>
                              <Label>Account Number</Label>
                              <Input
                                type="text"
                                value={account.accountNumber || ""}
                                onChange={(e) =>
                                  handleBankAccountChange(index, "accountNumber", e.target.value)
                                }
                                invalid={Boolean(validationErrors.bankAccounts[index]?.accountNumber)}
                              />
                              {validationErrors.bankAccounts[index]?.accountNumber ? (
                                <FormFeedback>{validationErrors.bankAccounts[index]?.accountNumber}</FormFeedback>
                              ) : null}
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup>
                              <Label>Bank</Label>
                              <Input
                                type="select"
                                value={account.bankId || ""}
                                onChange={(e) =>
                                  handleBankAccountChange(index, "bankId", e.target.value)
                                }
                                invalid={Boolean(validationErrors.bankAccounts[index]?.bankId)}
                              >
                                <option value="">Select bank</option>
                                {bankOptions.map((bank) => (
                                  <option key={bank.id} value={bank.id}>
                                    {bank.name}
                                  </option>
                                ))}
                              </Input>
                              {validationErrors.bankAccounts[index]?.bankId ? (
                                <FormFeedback>{validationErrors.bankAccounts[index]?.bankId}</FormFeedback>
                              ) : null}
                            </FormGroup>
                          </Col>
                          <Col md={2} className="d-flex align-items-end">
                            <Button
                              type="button"
                              size="sm"
                              color="danger"
                              className="mb-3"
                              title="Remove account"
                              onClick={() => removeBankAccount(index)}
                              disabled={(profile.bankAccounts || []).length <= 1}
                            >
                              <i className="ri-delete-bin-line" />
                            </Button>
                          </Col>
                        </React.Fragment>
                      ))}

                      <Col md={12}>
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">{headerSummary}</small>
                          <div className="d-flex gap-2">
                            <Button
                              type="submit"
                              style={{ backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE }}
                              disabled={isSaving}
                            >
                              {isSaving ? "Saving..." : "Save Profile"}
                            </Button>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CompanyProfile;
